/**
 * Axios API client with authentication interceptor.
 * Automatically injects Auth0 access token into all requests.
 */
import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Set the authorization token for API requests.
 * Call this after getting the access token from Auth0.
 *
 * @param token - JWT access token from Auth0
 */
export const setAuthToken = (token: string | null) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

/**
 * Clear the authorization token.
 * Call this on logout.
 */
export const clearAuthToken = () => {
  delete api.defaults.headers.common['Authorization'];
};

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;

      if (status === 401) {
        // Unauthorized - token expired or invalid
        console.error('Authentication error: Token may be expired');
        // Clear token and redirect to login handled by AuthContext
      } else if (status === 403) {
        // Forbidden - insufficient permissions
        console.error('Authorization error: Insufficient permissions');
      } else if (status === 404) {
        console.error('Not found:', error.response.config.url);
      } else if (status >= 500) {
        console.error('Server error:', error.response.data);
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('Network error: No response from server');
    } else {
      // Error in request configuration
      console.error('Request error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default api;
